import { databaseConfig } from '../../config/database';
import type { CartItem, PaymentMethod } from '../../domain/models';

const baseUrl = databaseConfig.mongodb.apiBaseUrl;

async function apiRequest<T>(
  path: string,
  accessToken: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    ...(options.headers as Record<string, string>),
  };

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers,
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      (body as { message?: string | string[] }).message ??
      response.statusText;
    throw new Error(
      Array.isArray(message) ? message.join(', ') : String(message),
    );
  }

  return body as T;
}

export interface BackendSale {
  id: string;
  visibleId: string;
  receiptId: string;
  cartId: string;
  cashierId: string;
  cashierName?: string;
  branchId: string;
  items: CartItem[];
  discountPercentage: number;
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  payment: {
    method: PaymentMethod;
    amountPaid: number;
    cashGiven?: number;
    cashReturned?: number;
  };
  shouldPrintReceipt: boolean;
  createdAt: string;
}

export interface CreateSalePayload {
  cartId: string;
  cashierId: string;
  branchId: string;
  items: CartItem[];
  discountPercentage: number;
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  payment: {
    method: PaymentMethod;
    amountPaid: number;
    cashGiven?: number;
    cashReturned?: number;
  };
  shouldPrintReceipt: boolean;
}

export async function createSale(
  accessToken: string,
  payload: CreateSalePayload,
): Promise<BackendSale> {
  // Strip FO-only UI fields (e.g. availableStock) — backend CartItemDto forbids extras
  const items = payload.items.map(
    ({
      productId,
      productName,
      unitPrice,
      taxRate,
      quantity,
      subtotal,
      productDiscountPercentage,
      productDiscountAmount,
    }) => ({
      productId,
      productName,
      unitPrice,
      taxRate,
      quantity,
      subtotal,
      productDiscountPercentage,
      productDiscountAmount,
    }),
  );

  const result = await apiRequest<{ data: BackendSale }>(
    '/api/sales',
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({ ...payload, items }),
    },
  );
  return result.data;
}

export async function listBranchSales(
  accessToken: string,
  branchId: string,
  limit = 100,
): Promise<BackendSale[]> {
  const result = await apiRequest<{ data: BackendSale[] }>(
    `/api/sales/branch/${branchId}?limit=${limit}`,
    accessToken,
    { method: 'GET' },
  );
  return result.data ?? [];
}

export async function getSaleByReceiptId(
  accessToken: string,
  receiptId: string,
): Promise<BackendSale> {
  const result = await apiRequest<{ data: BackendSale }>(
    `/api/sales/receipt/${encodeURIComponent(receiptId)}`,
    accessToken,
    { method: 'GET' },
  );
  return result.data;
}

export interface BackendRefund {
  id: string;
  visibleId: string;
  originalSaleId: string;
  originalReceiptId: string;
  branchId: string;
  refundAmount: number;
  collectedAmount?: number;
  items?: Array<{ productId: string; quantity: number }>;
  reason?: string;
  createdAt: string;
}

export async function listBranchRefunds(
  accessToken: string,
  branchId: string,
  limit = 100,
): Promise<BackendRefund[]> {
  const result = await apiRequest<{ data: BackendRefund[] }>(
    `/api/sales/refunds/branch/${encodeURIComponent(branchId)}?limit=${limit}`,
    accessToken,
    { method: 'GET' },
  );
  return result.data ?? [];
}

export interface CreateRefundPayload {
  originalSaleId: string;
  originalReceiptId: string;
  cashierId: string;
  branchId: string;
  items: CartItem[];
  refundAmount: number;
  collectedAmount?: number;
  exchanges?: Array<{
    originalProductId: string;
    replacementProductId: string;
    replacementName: string;
    quantity: number;
    unitPrice: number;
    taxRate: number;
  }>;
  reason?: string;
  settled?: boolean;
}

export async function createRefund(
  accessToken: string,
  payload: CreateRefundPayload,
): Promise<{ id: string; visibleId: string }> {
  const items = payload.items.map(
    ({
      productId,
      productName,
      unitPrice,
      taxRate,
      quantity,
      subtotal,
      productDiscountPercentage,
      productDiscountAmount,
    }) => ({
      productId,
      productName,
      unitPrice,
      taxRate,
      quantity,
      subtotal,
      productDiscountPercentage,
      productDiscountAmount,
    }),
  );

  const { settled: _settled, ...rest } = payload;
  const result = await apiRequest<{ data: { id: string; visibleId: string } }>(
    '/api/sales/refund',
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({ ...rest, items }),
    },
  );
  return result.data;
}

export interface CashClosurePreview {
  periodStart: string;
  periodEnd: string;
  openingFloat: number;
  cashSales: number;
  cardSales: number;
  chequeSales: number;
  refundsTotal: number;
  discountsTotal: number;
  taxesTotal: number;
  netSales: number;
  grandTotal: number;
  salesCount: number;
  receiptsCount: number;
  refundCount: number;
  expectedCash: number;
}

export interface CashClosureRecord {
  visibleId: string;
  branchId: string;
  managerName: string;
  difference: number;
  countedCash: number;
  extractedAmount: number;
  remainingFloat: number;
  confirmedAt: string;
  periodEnd: string;
  status: string;
}

export async function getCashClosurePreview(
  accessToken: string,
  branchId: string,
): Promise<CashClosurePreview> {
  const result = await apiRequest<{ data: CashClosurePreview }>(
    `/api/cash-closures/preview?branchId=${encodeURIComponent(branchId)}`,
    accessToken,
  );
  return result.data;
}

export async function getLastCashClosure(
  accessToken: string,
  branchId: string,
): Promise<CashClosureRecord | null> {
  const result = await apiRequest<{ data: CashClosureRecord | null }>(
    `/api/cash-closures/last?branchId=${encodeURIComponent(branchId)}`,
    accessToken,
  );
  return result.data;
}

export async function createCashClosure(
  accessToken: string,
  payload: {
    branchId: string;
    device: 'pos' | 'web' | 'mobile';
    countedCash: number;
    extractedAmount: number;
    denominations?: { label: string; value: number; qty: number }[];
    managerNotes?: string;
  },
): Promise<CashClosureRecord> {
  const result = await apiRequest<{ data: CashClosureRecord }>(
    '/api/cash-closures',
    accessToken,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return result.data;
}
